import React, { useState, useEffect } from "react";
import "../../Styles/SelfService.css";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUserData, setTickets } from "../../Pages/Store";
import { loginUser, getAllTickets, getErrorMessage } from "../../services/api";
import { encryptPassword } from "../../services/crypto";
import logoDark from "../../Assets/logo-dark.svg";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const path = location.pathname.toLowerCase();
    if (path === "/") {
      localStorage.removeItem("Login");
    }
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!username) errors.name = "Username required";
    if (!password) errors.password = "Password required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNavigate = (ticketData) => {
    navigate("/Self-Service", { state: { tickets: ticketData } });
  };

  const getUserDetails = async (event) => {
    event.preventDefault(); // Prevent the default form submission
    if (!validateForm()) return;

    try {
      const response = await loginUser(username, password);
      const user = response.data.user;

      toast.success("Login successful", {
        position: "top-right",
        autoClose: 3000,
      });

      // The password is never persisted or re-sent in plaintext. `ssCreds`
      // holds the ciphertext and every later API call forwards that as-is.
      const encryptedPassword = encryptPassword(password);

      localStorage.setItem("Id", user?.id);
      localStorage.setItem("Login", "true");
      localStorage.setItem("email", user?.email);
      localStorage.setItem("name", user?.name);
      localStorage.setItem(
        "ssCreds",
        JSON.stringify({ username, password: encryptedPassword }),
      );
      dispatch(setUserData({ ...user, customer_name: user?.name }));

      const ticketResponse = await getAllTickets(username, encryptedPassword);
      const tickets = ticketResponse.data.tickets;
      dispatch(setTickets(tickets));
      handleNavigate(tickets);
    } catch (error) {
      const message = getErrorMessage(error, "Login failed");
      toast.error(message, {
        position: "top-right",
        autoClose: 3000,
      });
      setUsername("");
      setPassword("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      getUserDetails(event);
    }
  };

  const handleSecOpsLogin = () => {
    window.open("https://secops.neuronsolutions.my/", "_blank");
  };

  return (
    <div className="login-wrapper">
      <div className="login-container">
        <div className="login-card">
          {/* Company Branding */}
          <div className="login-branding">
            <div className="brand-icon">
              <img src={logoDark} alt="Company Logo" className="brand-logo-img" />
            </div>
          </div>

          {/* Login Form */}
          <div className="login-form-container">
            <div className="form-intro">
              <h3 className="form-title">Welcome Back</h3>
              <p className="form-subtitle">Enter your credentials to access your account</p>
            </div>

            <form onSubmit={getUserDetails} className="login-form">
              <div className="form-group-custom">
                <label htmlFor="username" className="form-label-custom">
                  <span className="label-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  Username
                </label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    className={`form-control-custom ${formErrors.name ? 'is-invalid' : ''}`}
                    id="username"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  {formErrors.name && (
                    <div className="error-message">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <span>{formErrors.name}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group-custom">
                <label htmlFor="password" className="form-label-custom">
                  <span className="label-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  Password
                </label>
                <div className="input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    className={`form-control-custom ${password ? "has-toggle" : ""} ${formErrors.password ? 'is-invalid' : ''}`}
                    id="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  {password && (
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword((prev) => !prev)}
                      title={showPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M1 1l22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12S4.5 5 12 5s11 7 11 7-3.5 7-11 7S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  )}
                  {formErrors.password && (
                    <div className="error-message">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <span>{formErrors.password}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-custom-primary">
                  <span className="btn-text">Sign In</span>
                  <span className="btn-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </button>
              </div>

              {/* Alternative Login Options */}
              <div className="alternative-login-section">
                <div className="alternative-login-card" onClick={handleSecOpsLogin}>
                  <div className="alternative-login-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="alternative-login-content">
                    <h4 className="alternative-login-title">SecOps Portal</h4>
                    <p className="alternative-login-description">Click here to access</p>
                  </div>
                  <div className="alternative-login-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Footer branding */}
          <div className="login-powered-by">Powered by FrothDesk</div>
        </div>
      </div>
    </div>
  );
};

export default Login;